import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'

import { registerAccount } from '../api/auth'
import { ApiError } from '../api/client'
import { listCities, listProductTypesL0 } from '../api/reference'
import { trackMarketEvent } from '../analytics/marketEvents'
import { useAuth } from '../auth/AuthProvider'
import { AuthScreenLayout } from '../components/auth/AuthScreenLayout'
import { RegisterAuthField } from '../components/auth/RegisterAuthField'
import { SelectCombobox } from '../components/SelectCombobox'
import { isPivotModeEnabled } from '../pivot/pivotMode'
import { NOTIFICATIONS_CONSENT_CHECKBOX_LABEL, NOTIFICATIONS_CONSENT_PATH } from '../legal/notificationsConsent'
import {
  MARKETING_CONSENT_EXTERNAL_URL,
  PUBLIC_OFFER_PATH,
  REGISTER_PLAN_LABELS,
  parseRegisterPlan,
  readStoredRegisterPlan,
  storeRegisterPlan,
  type RegisterPlanId,
} from '../legal/publicOffer'
import {
  hasRegisterFormErrors,
  validateRegisterForm,
  type RegisterFormField,
  type RegisterFormFieldErrors,
} from '../utils/registerFormValidation'

/** Backend still expects a value; UI no longer collects horeca type. */
const DEFAULT_HORECA_TYPE = 'cafe'

export const RegisterScreen = () => {
  const { session, loading: authLoading, login } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const pivot = isPivotModeEnabled()
  const [orgType, setOrgType] = useState<'buyer' | 'supplier'>('buyer')
  const [plan, setPlan] = useState<RegisterPlanId>(() =>
    parseRegisterPlan(searchParams.get('plan') ?? readStoredRegisterPlan()),
  )
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [legalName, setLegalName] = useState('')
  const [inn, setInn] = useState('')
  const [city, setCity] = useState('perm')
  const [tradeName, setTradeName] = useState('')
  const [productTypeIds, setProductTypeIds] = useState<number[]>([])
  const [consentOffer, setConsentOffer] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [notificationsConsent, setNotificationsConsent] = useState(false)
  const [cities, setCities] = useState<{ code: string; label: string }[]>([])
  const [supplierTypes, setSupplierTypes] = useState<{ id: number; name: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<RegisterFormFieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (pivot) {
      setOrgType('buyer')
    }
  }, [pivot])

  useEffect(() => {
    const fromQuery = searchParams.get('plan')
    const next = parseRegisterPlan(fromQuery ?? readStoredRegisterPlan())
    setPlan(next)
    storeRegisterPlan(next)
    trackMarketEvent('register_view', { plan: next })
    trackMarketEvent('plan_selected', { plan: next, source: fromQuery ? 'query' : 'default' })
  }, [searchParams])

  useEffect(() => {
    void listCities()
      .then(setCities)
      .catch(() => setCities([]))
    if (pivot) {
      return
    }
    void listProductTypesL0()
      .then((typeOptions) => {
        setSupplierTypes(typeOptions)
        if (typeOptions[0]) {
          setProductTypeIds([typeOptions[0].id])
        }
      })
      .catch(() => setSupplierTypes([]))
  }, [pivot])

  const clearFieldError = (field: RegisterFormField) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current
      }
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  const focusFirstInvalidField = () => {
    requestAnimationFrame(() => {
      const invalidInput = document.querySelector<HTMLElement>(
        '.market-auth-field--invalid .market-auth-field__input, .market-auth-fieldset--invalid',
      )
      invalidInput?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      if (invalidInput instanceof HTMLInputElement || invalidInput instanceof HTMLSelectElement) {
        invalidInput.focus()
        return
      }
      invalidInput?.querySelector<HTMLElement>('.market-auth-field__input')?.focus()
    })
  }

  if (session) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const validationErrors = validateRegisterForm({
      orgType: pivot ? 'buyer' : orgType,
      email,
      password,
      inn,
      legalName,
      tradeName,
      consentOffer,
      productTypeIds,
    })
    if (hasRegisterFormErrors(validationErrors)) {
      setFieldErrors(validationErrors)
      setFormError(null)
      focusFirstInvalidField()
      return
    }
    setFieldErrors({})
    setLoading(true)
    setFormError(null)
    const effectiveOrgType = pivot ? 'buyer' : orgType
    trackMarketEvent('register_submit', { plan })
    try {
      const result = await registerAccount({
        org_type: effectiveOrgType,
        email,
        password,
        legal_name: legalName.trim(),
        inn: inn.trim(),
        city,
        trade_name: tradeName.trim(),
        consent_offer: true,
        marketing_consent: marketingConsent,
        notifications_consent: notificationsConsent,
        plan,
        ...(effectiveOrgType === 'buyer' ? { horeca_type: DEFAULT_HORECA_TYPE } : {}),
        ...(effectiveOrgType === 'supplier' ? { product_type_ids: productTypeIds } : {}),
      })
      if (result.trialStarted) {
        trackMarketEvent('trial_start', { plan: result.plan })
      }
      await login(email, password)
      navigate(result.redirectPath || '/', { replace: true })
    } catch (registerError) {
      if (registerError instanceof ApiError) {
        setFormError(registerError.message || 'Не удалось зарегистрироваться')
      } else {
        setFormError(
          registerError instanceof Error ? registerError.message : 'Не удалось зарегистрироваться',
        )
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthScreenLayout variant="register" loading={authLoading}>
      <div className="market-auth__card market-auth__card--register">
        <header className="market-auth__card-header market-auth__card-header--compact">
          <h2 className="market-auth__card-title market-auth__card-title--compact">Регистрация</h2>
          <p className="market-auth__card-lead market-auth__card-lead--compact">
            {pivot ? 'Создайте аккаунт ресторана' : 'Создайте аккаунт ресторана или поставщика'}
          </p>
          <p className="market-auth__card-lead market-auth__card-lead--compact">
            Тариф: <strong>{REGISTER_PLAN_LABELS[plan]}</strong>
          </p>
        </header>

        <form
          className="market-auth__form market-auth__form--register"
          noValidate
          onSubmit={(event) => void handleSubmit(event)}
        >
          {!pivot ? (
            <div className="market-auth-field market-auth-field--compact market-auth__form-span-full">
              <SelectCombobox
                label="Тип аккаунта"
                value={orgType}
                inputClassName="market-auth-field__input market-auth-field__input--select"
                options={[
                  { value: 'buyer', label: 'Покупатель (HoReCa)' },
                  { value: 'supplier', label: 'Поставщик' },
                ]}
                onChange={(next) => setOrgType(next as 'buyer' | 'supplier')}
              />
            </div>
          ) : null}

          <RegisterAuthField
            field="email"
            error={fieldErrors.email}
            label={
              <>
                Корпоративная почта <span className="market-auth-field__required">*</span>
              </>
            }
          >
            <input
              className="market-auth-field__input"
              type="email"
              value={email}
              placeholder="zakupki@company.ru"
              aria-invalid={fieldErrors.email ? true : undefined}
              aria-describedby={fieldErrors.email ? 'register-email-error' : undefined}
              onChange={(event) => {
                setEmail(event.target.value)
                clearFieldError('email')
              }}
            />
          </RegisterAuthField>

          <RegisterAuthField
            field="password"
            error={fieldErrors.password}
            label={
              <>
                Пароль <span className="market-auth-field__required">*</span>
              </>
            }
          >
            <input
              className="market-auth-field__input"
              type="password"
              value={password}
              placeholder="Мин. 8 символов"
              aria-invalid={fieldErrors.password ? true : undefined}
              aria-describedby={fieldErrors.password ? 'register-password-error' : undefined}
              onChange={(event) => {
                setPassword(event.target.value)
                clearFieldError('password')
              }}
            />
          </RegisterAuthField>

          <RegisterAuthField
            field="inn"
            error={fieldErrors.inn}
            label={
              <>
                ИНН <span className="market-auth-field__required">*</span>
              </>
            }
          >
            <input
              className="market-auth-field__input"
              inputMode="numeric"
              value={inn}
              aria-invalid={fieldErrors.inn ? true : undefined}
              aria-describedby={fieldErrors.inn ? 'register-inn-error' : undefined}
              onChange={(event) => {
                setInn(event.target.value)
                clearFieldError('inn')
              }}
            />
          </RegisterAuthField>

          <RegisterAuthField
            field="legalName"
            error={fieldErrors.legalName}
            label={
              <>
                Юридическое название <span className="market-auth-field__required">*</span>
              </>
            }
          >
            <input
              className="market-auth-field__input"
              value={legalName}
              placeholder='ООО «Название»'
              aria-invalid={fieldErrors.legalName ? true : undefined}
              aria-describedby={fieldErrors.legalName ? 'register-legalName-error' : undefined}
              onChange={(event) => {
                setLegalName(event.target.value)
                clearFieldError('legalName')
              }}
            />
          </RegisterAuthField>

          <RegisterAuthField
            field="tradeName"
            error={fieldErrors.tradeName}
            label={
              <>
                Торговое название <span className="market-auth-field__required">*</span>
              </>
            }
          >
            <input
              className="market-auth-field__input"
              value={tradeName}
              aria-invalid={fieldErrors.tradeName ? true : undefined}
              aria-describedby={fieldErrors.tradeName ? 'register-tradeName-error' : undefined}
              onChange={(event) => {
                setTradeName(event.target.value)
                clearFieldError('tradeName')
              }}
            />
          </RegisterAuthField>

          <div className="market-auth-field market-auth-field--compact market-auth-field--combobox">
            <SelectCombobox
              label="Город"
              value={city}
              inputClassName="market-auth-field__input market-auth-field__input--select"
              options={cities.map((option) => ({ value: option.code, label: option.label }))}
              onChange={setCity}
              required
              disabled={cities.length === 0}
              placeholder={cities.length === 0 ? 'Загрузка городов…' : 'Выберите…'}
            />
          </div>

          {orgType === 'supplier' ? (
            <fieldset
              className={`market-auth-fieldset market-auth-fieldset--categories market-auth__form-span-full${fieldErrors.productTypeIds ? ' market-auth-fieldset--invalid' : ''}`}
            >
              <legend className="market-auth-field__label">Категории товаров</legend>
              <div className="market-auth-checkbox-grid market-auth-checkbox-grid--register">
                {supplierTypes.map((type) => (
                  <label key={type.id} className="market-auth-checkbox market-auth-checkbox--category">
                    <input
                      type="checkbox"
                      checked={productTypeIds.includes(type.id)}
                      onChange={() => {
                        setProductTypeIds((current) =>
                          current.includes(type.id)
                            ? current.filter((id) => id !== type.id)
                            : [...current, type.id],
                        )
                        clearFieldError('productTypeIds')
                      }}
                    />
                    <span>{type.name}</span>
                  </label>
                ))}
              </div>
              {fieldErrors.productTypeIds ? (
                <span
                  className="market-auth-field__error"
                  id="register-productTypeIds-error"
                  role="alert"
                >
                  {fieldErrors.productTypeIds}
                </span>
              ) : null}
            </fieldset>
          ) : null}

          <div className="market-auth__register-actions market-auth__form-span-full">
            <div className="market-auth__legal-consents" aria-label="Согласия">
              <div className={fieldErrors.consentOffer ? 'market-auth-field--invalid' : ''}>
                <label className="market-auth-checkbox market-auth-checkbox--consent">
                  <input
                    type="checkbox"
                    checked={consentOffer}
                    aria-invalid={fieldErrors.consentOffer ? true : undefined}
                    aria-describedby={
                      fieldErrors.consentOffer ? 'register-consent-offer-error' : undefined
                    }
                    onChange={(event) => {
                      setConsentOffer(event.target.checked)
                      clearFieldError('consentOffer')
                    }}
                  />
                  <span>
                    Принимаю{' '}
                    <Link
                      className="market-auth-checkbox__doc-link"
                      to={PUBLIC_OFFER_PATH}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Публичную оферту
                    </Link>
                  </span>
                </label>
                {fieldErrors.consentOffer ? (
                  <span
                    className="market-auth-field__error"
                    id="register-consent-offer-error"
                    role="alert"
                  >
                    {fieldErrors.consentOffer}
                  </span>
                ) : null}
              </div>

              <label className="market-auth-checkbox market-auth-checkbox--consent">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(event) => setMarketingConsent(event.target.checked)}
                />
                <span>
                  Согласие на рекламные сообщения (
                  <a
                    className="market-auth-checkbox__doc-link"
                    href={MARKETING_CONSENT_EXTERNAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ознакомиться
                  </a>
                  ) — необязательно
                </span>
              </label>

              <label className="market-auth-checkbox market-auth-checkbox--consent">
                <input
                  type="checkbox"
                  checked={notificationsConsent}
                  onChange={(event) => setNotificationsConsent(event.target.checked)}
                />
                <span>
                  {NOTIFICATIONS_CONSENT_CHECKBOX_LABEL} (
                  <Link
                    className="market-auth-checkbox__doc-link"
                    to={NOTIFICATIONS_CONSENT_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ознакомиться
                  </Link>
                  )
                </span>
              </label>
            </div>

            {formError ? (
              <p className="echat-error market-auth__error" role="alert">
                {formError}
              </p>
            ) : null}

            <button type="submit" className="echat-btn echat-btn--primary market-auth__submit" disabled={loading}>
              {loading ? 'Регистрация…' : 'Зарегистрироваться'}
            </button>
          </div>
        </form>

        <div className="market-auth__footer market-auth__footer--register">
          <p className="market-auth__signup market-auth__signup--in-card">
            Уже есть аккаунт?{' '}
            <Link className="market-auth__signup-link" to="/login">
              Войти
            </Link>
          </p>
        </div>
      </div>
    </AuthScreenLayout>
  )
}
